import React, { lazy, Suspense, useEffect, useState } from 'react';
import {
    Adas,
    BodyElectronics,
    DefaultAutomotive,
    Infotainment,
    PowerTrain,
    SafetyAndSecurity,
    VehicleElectrification,
} from './CarImageComponent';
import AutomotiveBreadcrumbs from './AutomotiveBreadcrumbs';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApplicationsAutomotiveContent } from '../../graphql/pimcore/modules/siteContent/types/ApplicationsContent';

const DigitalPassiveImage = lazy(() =>
    import('./CarImageComponent').then((module) => ({ default: module.DigitalPassiveImage }))
);
const TractionInverterImage = lazy(() =>
    import('./CarImageComponent').then((module) => ({ default: module.TractionInverterImage }))
);
const BmsLowVoltImage = lazy(() =>
    import('./CarImageComponent').then((module) => ({ default: module.BmsLowVoltImage }))
);
const DcdcConverterImage = lazy(() =>
    import('./CarImageComponent').then((module) => ({ default: module.DcdcConverterImage }))
);
const PowerConversionImage = lazy(() =>
    import('./CarImageComponent').then((module) => ({ default: module.PowerConversionImage }))
);
const BmsLowVoltRelativeImage = lazy(() =>
    import('./CarImageComponent').then((module) => ({ default: module.BmsLowVoltRelative }))
);
type ComponentName = keyof typeof components;

interface ComponentInfo {
    label: string;
    component?: React.ComponentType<any>;
    imageComponent?: React.ComponentType<any>;
}

const components: { [key: string]: ComponentInfo } = {
    // Top-level components
    automotive: {
        label: 'Automotive',
        component: DefaultAutomotive,
    },
    adas: {
        label: 'ADAS',
        component: Adas,
    },
    powertrain: {
        label: 'Powertrain',
        component: PowerTrain,
    },
    vehicleelectrification: {
        label: 'Vehicle Electrification',
        component: VehicleElectrification,
    },
    infotainment: {
        label: 'Infotainment',
        component: Infotainment,
    },
    safetyandsecurity: {
        label: 'Safety & Security',
        component: SafetyAndSecurity,
    },
    bodyelectronics: {
        label: 'Body Electronics',
        component: BodyElectronics,
    },
    // Components mapped to LowVoltDcImage
    tcu: {
        label: 'Transmission Control Unit (TCU)',
        imageComponent: DigitalPassiveImage,
    },
    directinjection: {
        label: 'Fuel Injection System (Direct Injection)',
        imageComponent: DigitalPassiveImage,
    },
    throttlecontrol: {
        label: 'Throttle Control',
        imageComponent: DigitalPassiveImage,
    },
    ecu: {
        label: 'Engine Control Unit (ECU)',
        imageComponent: DigitalPassiveImage,
    },
    vvt: {
        label: 'Variable Valve Timing (VVT)',
        imageComponent: DigitalPassiveImage,
    },
    radar: {
        label: 'Radar',
        imageComponent: DigitalPassiveImage,
    },
    lidar: {
        label: 'Lidar',
        imageComponent: DigitalPassiveImage,
    },
    cameras: {
        label: 'Cameras',
        imageComponent: DigitalPassiveImage,
    },
    adaptivelighting: {
        label: 'Adaptive Lighting',
        imageComponent: DigitalPassiveImage,
    },
    ultrasonicsensors: {
        label: 'Ultra Sonic Sensors',
        imageComponent: DigitalPassiveImage,
    },
    instrumentcluster: {
        label: 'Instrument Cluster',
        imageComponent: DigitalPassiveImage,
    },
    headupdisplay: {
        label: 'Head-Up Display',
        imageComponent: DigitalPassiveImage,
    },
    navigation: {
        label: 'Navigation',
        imageComponent: DigitalPassiveImage,
    },
    nightvision: {
        label: 'Night Vision',
        imageComponent: DigitalPassiveImage,
    },
    keylessentry: {
        label: 'Keyless Entry',
        imageComponent: DigitalPassiveImage,
    },
    antitheftgps: {
        label: 'Anti-Theft GPS',
        imageComponent: DigitalPassiveImage,
    },
    airbagdeployment: {
        label: 'Airbag Deployment',
        imageComponent: DigitalPassiveImage,
    },
    autodimmingmirror: {
        label: 'Auto Dimming Mirror',
        imageComponent: DigitalPassiveImage,
    },
    windshieldwipercontrol: {
        label: 'Windshield Wiper Control',
        imageComponent: DigitalPassiveImage,
    },
    obdii: {
        label: 'OBD II',
        imageComponent: DigitalPassiveImage,
    },
    telematiccontrolunit: {
        label: 'Telematic Control Unit',
        imageComponent: DigitalPassiveImage,
    },
    tpms: {
        label: 'TPMS',
        imageComponent: DigitalPassiveImage,
    },
    electricpowersteering: {
        label: 'Electric Power Steering',
        imageComponent: TractionInverterImage,
    },
    wirelesscharging: {
        label: 'Wireless Charging',
        imageComponent: DigitalPassiveImage,
    },
    windows: {
        label: 'Windows',
        imageComponent: DigitalPassiveImage,
    },
    sunroof: {
        label: 'Sunroof',
        imageComponent: DigitalPassiveImage,
    },
    wireharness: {
        label: 'Wire Harness',
        imageComponent: DigitalPassiveImage,
    },
    seatcontrol: {
        label: 'Seat Control',
        imageComponent: DigitalPassiveImage,
    },
    // Components mapped to TractionInverterImage
    radiatorfan: {
        label: 'Radiator Fan',
        imageComponent: TractionInverterImage,
    },
    eturboeboost: {
        label: 'eTurbo/eBoost',
        imageComponent: TractionInverterImage,
    },
    egr: {
        label: 'Exhaust Gas Recirculation (EGR)',
        imageComponent: DigitalPassiveImage,
    },
    electricwaterpump: {
        label: 'Electric Water Pump',
        imageComponent: TractionInverterImage,
    },
    electriccompressor: {
        label: 'Electric Compressor',
        imageComponent: TractionInverterImage,
    },
    electricmotorgenerator: {
        label: 'Electric Motor/Generator',
        imageComponent: TractionInverterImage,
    },
    tractioninverter: {
        label: 'Traction Inverter',
        imageComponent: TractionInverterImage,
    },
    // Components mapped to BmsLowVoltImage
    fuelpump: {
        label: 'Fuel Pump',
        imageComponent: DigitalPassiveImage,
    },
    onboardcharger: {
        label: 'Onboard Charger',
        imageComponent: PowerConversionImage,
    },
    batterymanagementsystem: {
        label: 'Battery Management System',
        imageComponent: BmsLowVoltRelativeImage,
    },
    // Components mapped to DcdcConverterImage
    ignitionsystem: {
        label: 'Ignition System',
        imageComponent: DcdcConverterImage,
    },
    dcdcconverter: {
        label: 'DC/DC Converter',
        imageComponent: DcdcConverterImage,
    },
    // Components mapped to PowerConversionImage
    vehiclewirelesscharging: {
        label: 'Vehicle Wireless Charging',
        imageComponent: PowerConversionImage,
    },
};

const generateLabelMap = (
    cmsContent: ApplicationsAutomotiveContent
): Map<string | number, string> => {
    return new Map([
        ['automotive', cmsContent.Automotive.title.content],
        ['adas', cmsContent.Automotive.imageMapLinkTexts.adas.content],
        ['powertrain', cmsContent.Automotive.imageMapLinkTexts.powertrain.content],
        [
            'vehicleelectrification',
            cmsContent.Automotive.imageMapLinkTexts.vehicleElectrification.content,
        ],
        ['infotainment', cmsContent.Automotive.imageMapLinkTexts.infotainment.content],
        ['safetyandsecurity', cmsContent.Automotive.imageMapLinkTexts.safetyAndSecurity.content],
        ['bodyelectronics', cmsContent.Automotive.imageMapLinkTexts.bodyElectronics.content],
        ['tcu', cmsContent.Powertrain.imageMapLinkTexts.transmissionControlUnit.content],
        ['directinjection', cmsContent.Powertrain.imageMapLinkTexts.fuelInjectionSystem.content],
        ['throttlecontrol', cmsContent.Powertrain.imageMapLinkTexts.throttleControl.content],
        ['ecu', cmsContent.Powertrain.imageMapLinkTexts.engineControlUnit.content],
        ['vvt', cmsContent.Powertrain.imageMapLinkTexts.variableValveTiming.content],
        ['radar', cmsContent.ADAS.imageMapLinkTexts.radar.content],
        ['lidar', cmsContent.ADAS.imageMapLinkTexts.lidar.content],
        ['cameras', cmsContent.ADAS.imageMapLinkTexts.cameras.content],
        ['adaptivelighting', cmsContent.ADAS.imageMapLinkTexts.adaptiveLighting.content],
        ['ultrasonicsensors', cmsContent.ADAS.imageMapLinkTexts.ultraSonicSensors.content],
        ['instrumentcluster', cmsContent.Infotainment.imageMapLinkTexts.instrumentCluster.content],
        ['headupdisplay', cmsContent.Infotainment.imageMapLinkTexts.headUpDisplay.content],
        ['navigation', cmsContent.Infotainment.imageMapLinkTexts.navigation.content],
        ['nightvision', cmsContent.Infotainment.imageMapLinkTexts.nightVision.content],
        ['keylessentry', cmsContent.SafetyAndSecurity.imageMapLinkTexts.keylessEntry.content],
        ['antitheftgps', cmsContent.SafetyAndSecurity.imageMapLinkTexts.antiTheftGPS.content],
        [
            'airbagdeployment',
            cmsContent.SafetyAndSecurity.imageMapLinkTexts.airbagDeployment.content,
        ],
        [
            'autodimmingmirror',
            cmsContent.SafetyAndSecurity.imageMapLinkTexts.autoDimmingMirror.content,
        ],
        [
            'windshieldwipercontrol',
            cmsContent.SafetyAndSecurity.imageMapLinkTexts.windshieldWiperControl.content,
        ],
        ['obdii', cmsContent.SafetyAndSecurity.imageMapLinkTexts.obd2.content],
        [
            'telematiccontrolunit',
            cmsContent.SafetyAndSecurity.imageMapLinkTexts.telematicControlUnit.content,
        ],
        ['tpms', cmsContent.SafetyAndSecurity.imageMapLinkTexts.tpms.content],
        [
            'electricpowersteering',
            cmsContent.BodyElectronics.imageMapLinkTexts.electricPowerSteering.content,
        ],
        ['wirelesscharging', cmsContent.BodyElectronics.imageMapLinkTexts.wirelessCharging.content],
        ['windows', cmsContent.BodyElectronics.imageMapLinkTexts.windows.content],
        ['sunroof', cmsContent.BodyElectronics.imageMapLinkTexts.sunroof.content],
        ['wireharness', cmsContent.BodyElectronics.imageMapLinkTexts.wireHarness.content],
        ['seatcontrol', cmsContent.BodyElectronics.imageMapLinkTexts.seatControl.content],
        ['radiatorfan', cmsContent.Powertrain.imageMapLinkTexts.radiatorFan.content],
        ['eturboeboost', cmsContent.Powertrain.imageMapLinkTexts.eTurboAndEBoost.content],
        ['egr', cmsContent.Powertrain.imageMapLinkTexts.exhaustGasRecirculation.content],
        [
            'electricwaterpump',
            cmsContent.BodyElectronics.imageMapLinkTexts.electricWaterPump.content,
        ],
        [
            'electriccompressor',
            cmsContent.BodyElectronics.imageMapLinkTexts.electricCompressor.content,
        ],
        [
            'electricmotorgenerator',
            cmsContent.VehicleElectrification.imageMapLinkTexts.electricMotorGenerator.content,
        ],
        [
            'tractioninverter',
            cmsContent.VehicleElectrification.imageMapLinkTexts.tractionInverter.content,
        ],
        ['fuelpump', cmsContent.Powertrain.imageMapLinkTexts.fuelPump.content],
        [
            'onboardcharger',
            cmsContent.VehicleElectrification.imageMapLinkTexts.onboardCharger.content,
        ],
        [
            'batterymanagementsystem',
            cmsContent.VehicleElectrification.imageMapLinkTexts.batteryManagementSystem.content,
        ],
        ['ignitionsystem', cmsContent.Powertrain.imageMapLinkTexts.ignitionSystem.content],
        [
            'dcdcconverter',
            cmsContent.VehicleElectrification.imageMapLinkTexts.dcDcConverter.content,
        ],
        [
            'vehiclewirelesscharging',
            cmsContent.VehicleElectrification.imageMapLinkTexts.vehicleWirelessCharging.content,
        ],
    ]);
};

interface AutomotiveDisplayProps {
    cmsContent: ApplicationsAutomotiveContent;
}

const AutomotiveDisplay: React.FC<AutomotiveDisplayProps> = ({ cmsContent }) => {
    const root = '/applications/automotive';
    const navigate = useNavigate();
    const location = useLocation();
    const labelMap = generateLabelMap(cmsContent);

    const [componentStack, setComponentStack] = useState<ComponentName[]>(['automotive']);

    useEffect(() => {
        const pathSegments = location.pathname.toLowerCase().split('/').filter(Boolean);

        const automotiveIndex = pathSegments.indexOf('automotive');

        let relevantSegments: string[] = [];
        if (automotiveIndex !== -1) {
            relevantSegments = pathSegments.slice(automotiveIndex + 1);
        }

        if (relevantSegments.length === 0) {
            setComponentStack(['automotive']);
        } else {
            const componentNames: ComponentName[] = relevantSegments.map((segment) => {
                const key = segment.toLowerCase();
                if (components[key]) {
                    return key as ComponentName;
                } else {
                    return 'automotive';
                }
            });
            setComponentStack(['automotive', ...componentNames]);
        }
    }, [location.pathname]);

    useEffect(() => {
        if (location.state?.scrollPosition) {
            window.scrollTo(0, location.state.scrollPosition);
        }
    }, [location]);

    const handleComponentSwitch = (componentName: ComponentName) => {
        const scrollPosition = window.scrollY;
        const newComponentStack = [...componentStack, componentName];
        setComponentStack(newComponentStack);
        const path = `/applications/automotive/${newComponentStack.slice(1).join('/')}`;
        navigate(path, { state: { scrollPosition } });
    };

    const handleBack = () => {
        const scrollPosition = window.scrollY;
        if (componentStack.length > 1) {
            const newComponentStack = componentStack.slice(0, -1);
            setComponentStack(newComponentStack);
            const path = `/applications/automotive/${newComponentStack.slice(1).join('/')}`;
            navigate(path, { state: { scrollPosition } });
        } else {
            setComponentStack(['automotive']);
            navigate(`/applications/automotive`, { state: { scrollPosition } });
        }
    };

    const fadeVariants = {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
    };

    const fadeTransition = {
        duration: 0.8,
        ease: 'easeInOut',
    };

    const handleBreadcrumbClick = (index: number) => {
        const newComponentStack = componentStack.slice(0, index + 1);
        const scrollPosition = window.scrollY;

        setComponentStack(newComponentStack);
        const path = `/applications/automotive/${newComponentStack.slice(1).join('/')}`;
        navigate(path, { state: { scrollPosition } });
    };

    const currentComponentName = componentStack[componentStack.length - 1];

    const currentComponentInfo = currentComponentName
        ? components[currentComponentName]
        : undefined;

    const breadcrumbItems = componentStack.map((componentName, index) => ({
        label: (labelMap.get(componentName) || componentName).toString(),
        href: `${root}/${componentStack.slice(0, index + 1).join('/')}`,
    }));

    const commonProps = {
        onBack: handleBack,
        onAnnotationClick: handleComponentSwitch,
    };
    return (
        <>
            <AutomotiveBreadcrumbs
                items={breadcrumbItems}
                onBreadcrumbClick={handleBreadcrumbClick}
            />
            <Typography
                sx={{ marginBottom: '2rem', fontFamily: 'Poppins', fontSize: '16px' }}
            ></Typography>
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentComponentName}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={fadeVariants}
                    transition={fadeTransition}
                >
                    <Suspense fallback={<div>Loading...</div>}>
                        {currentComponentInfo?.imageComponent ? (
                            <currentComponentInfo.imageComponent
                                onBack={handleBack}
                                cmsContent={cmsContent}
                            />
                        ) : currentComponentInfo?.component ? (
                            <currentComponentInfo.component {...commonProps} labelMap={labelMap} />
                        ) : null}
                    </Suspense>
                </motion.div>
            </AnimatePresence>
        </>
    );
};

export default AutomotiveDisplay;
